import { useState, useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Clock, Sparkles } from "lucide-react";
import Nav from "../components/landing/Nav";
import Footer from "../components/landing/Footer";
import ContactDialog from "../components/landing/ContactDialog";
import ShareBar from "../components/landing/ShareBar";
import { findArticle } from "../lib/articles";

export default function Article() {
  const { slug } = useParams();
  const article = findArticle(slug);
  const [contactOpen, setContactOpen] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [slug]);

  if (!article) return <Navigate to="/" replace />;

  const Body = article.Component;
  const url =
    typeof window !== "undefined"
      ? `${window.location.origin}/learn/${article.slug}`
      : `https://nztaxation.nz/learn/${article.slug}`;

  return (
    <main className="min-h-screen bg-[#FAFAFA] text-[#0F172A]" data-testid="article-page">
      <Nav
        onCtaClick={() => (window.location.href = "/#pricing")}
        onContactClick={() => setContactOpen(true)}
      />

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-slate-200">
        <div className="absolute inset-0 bg-grid opacity-60 pointer-events-none" />
        <div className="relative max-w-4xl mx-auto px-6 md:px-10 pt-12 md:pt-20 pb-12 md:pb-16">
          <Link
            to="/#learn"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] font-bold text-slate-500 hover:text-[#C9A349] transition-colors"
            data-testid="article-back"
          >
            <ArrowLeft className="w-3 h-3" /> Back to free guides
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mt-8 flex flex-wrap items-center gap-3"
          >
            <span className="inline-flex items-center gap-2 border border-slate-300 px-3 py-1.5 text-[11px] uppercase tracking-[0.22em] font-bold text-slate-700 bg-white/70 backdrop-blur">
              <Sparkles className="w-3 h-3 text-[#C9A349]" />
              {article.heroKicker}
            </span>
            <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500">
              <Clock className="w-3.5 h-3.5" /> {article.readMinutes} min read
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.05 }}
            className="mt-6 font-heading font-black text-4xl sm:text-5xl md:text-6xl tracking-tight leading-[1.02]"
            data-testid="article-headline"
          >
            {article.headline[0]}
            <br />
            <span className="text-[#C9A349]">{article.headline[1]}</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.12 }}
            className="mt-6 text-lg text-slate-600 max-w-2xl leading-relaxed"
          >
            {article.description}
          </motion.p>

          <div className="mt-8">
            <ShareBar url={url} title={article.title} description={article.socialDescription} />
          </div>
        </div>
      </section>

      {/* Article body */}
      <section className="bg-white border-b border-slate-200">
        <article className="max-w-3xl mx-auto px-6 md:px-10 py-14 md:py-20 prose-narrow">
          <Body />
        </article>
      </section>

      {/* Closing CTA */}
      <section className="bg-[#0A192F] text-white">
        <div className="max-w-5xl mx-auto px-6 md:px-10 py-16 md:py-24">
          <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#C9A349]">
            Ready to never worry about GST again?
          </div>
          <h2 className="mt-4 font-heading font-black text-3xl md:text-5xl tracking-tight leading-[1.05]">
            Monthly tax estimates, in your inbox.
            <br />
            <span className="text-[#C9A349]">No more March panic.</span>
          </h2>
          <p className="mt-5 text-slate-300 max-w-2xl text-lg">
            Pick a plan, connect your books, and we'll email you your tax position every month
            — including how much to set aside for GST. From $59 + GST/month, cancel any time.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to="/#pricing"
              className="inline-flex items-center gap-2 bg-[#C9A349] text-[#0A192F] px-6 py-4 font-heading font-bold uppercase tracking-[0.12em] text-sm hover:bg-white transition-colors"
              data-testid="article-cta-pricing"
            >
              See monthly plans <ArrowRight className="w-4 h-4" />
            </Link>
            <button
              onClick={() => setContactOpen(true)}
              className="inline-flex items-center gap-2 border border-white/30 px-6 py-4 font-medium hover:bg-white/10 transition-colors"
              data-testid="article-cta-email"
            >
              Email the team
            </button>
          </div>

          <div className="mt-12">
            <ShareBar url={url} title={article.title} description={article.socialDescription} />
          </div>
        </div>
      </section>

      <Footer />
      <ContactDialog open={contactOpen} onOpenChange={setContactOpen} />
    </main>
  );
}
